import {
  QrCodeIcon,
  BookOpenIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  ArrowDownTrayIcon,
  ChartBarIcon,
  UserGroupIcon,
  ArrowPathIcon,
  LockClosedIcon,
  ComputerDesktopIcon,
  GlobeAltIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { useInView } from "../hooks/useInView";

const primaryFeatures = [
  {
    icon: QrCodeIcon,
    title: "Barcode scanning",
    desc: "Plug in a barcode scanner and it just works — no setup, no fiddling. Scan borrower cards and book barcodes one after another. The screen tells helpers exactly what to scan next.",
    detail: "Works with any standard USB barcode scanner (from about \u00a320).",
  },
  {
    icon: BookOpenIcon,
    title: "ISBN auto-lookup",
    desc: "Scan a barcode and Bookburrow fills in the title, author, publisher, and page count automatically. It remembers every book it's seen, so the same barcode never needs looking up twice.",
    detail: "Checks multiple sources including Open Library and Google Books to find a match.",
  },
  {
    icon: MagnifyingGlassIcon,
    title: "Instant search",
    desc: "Find any book by title, author, or barcode number. Filter by availability — show all books, available only, or currently on loan. Results appear as you type.",
    detail: "Searches thousands of books without any noticeable delay.",
  },
  {
    icon: ChartBarIcon,
    title: "Reports & exports",
    desc: "See what matters: late books, most popular titles, reading activity by year group, stock summary, recently added, and never-lent items. Print them out or open in Excel.",
    detail: "Share reading data with your head teacher or governors — no pivot tables needed.",
  },
  {
    icon: ArrowDownTrayIcon,
    title: "Automatic backups",
    desc: "Bookburrow backs itself up every 15 minutes to a folder you choose — OneDrive, SharePoint, a USB stick, or a network drive. One-click restore from any backup file.",
    detail: "30-day retention by default. Hardware failure means at most 15 minutes of lost work.",
  },
  {
    icon: UserGroupIcon,
    title: "Borrower management",
    desc: "Add pupils individually or import a list from your school system — SIMS, Arbor, Bromcom all work. Year-end promotion advances year groups and marks leavers in one click.",
    detail: "GDPR-compliant: loan history retained for the current year, purged at year-end.",
  },
];

const secondaryFeatures = [
  { icon: LockClosedIcon, title: "PIN-based access", desc: "Simple 4-digit PIN, not passwords. Helpers see lend and return; teachers unlock settings and reports." },
  { icon: ShieldCheckIcon, title: "Works without Wi-Fi", desc: "Lending, returns, and search all work without any internet connection." },
  { icon: ComputerDesktopIcon, title: "Works on any computer", desc: "Runs on Windows and Linux. One download, nothing else to install." },
  { icon: ArrowPathIcon, title: "Automatic updates", desc: "While your support is active, new versions install on their own — you won't even notice. No IT department needed." },
  { icon: GlobeAltIcon, title: "Nothing else to manage", desc: "Everything lives on one computer. No cloud accounts, no per-pupil fees." },
  { icon: ClockIcon, title: "5-minute onboarding", desc: "A parent helper or TA can lend and return books within five minutes of sitting down." },
];

export function Features() {
  const heroView = useInView<HTMLElement>(0.05);
  const gridView = useInView<HTMLElement>(0.05);
  const platesView = useInView<HTMLElement>(0.05);
  const secondaryView = useInView<HTMLElement>(0.05);

  return (
    <div className="editorial-page">
      {/* ── Hero ──────────────────────────────── */}
      <section
        ref={heroView.ref}
        className="page-hero grain relative overflow-hidden bg-cream-50 pb-20 pt-16 md:pb-28 md:pt-20"
      >
        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <div className={`max-w-3xl reveal ${heroView.isVisible ? "visible" : ""}`}>
            <span className="text-sm font-semibold uppercase tracking-widest text-forest-500">
              Features
            </span>
            <h1 className="mt-4 font-display text-4xl font-semibold leading-tight tracking-tight text-forest-950 sm:text-5xl lg:text-6xl">
              Everything your library needs,{" "}
              <em className="italic text-forest-600">nothing it doesn't.</em>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-bark-600">
              You didn't train to be a librarian — and you shouldn't have to.
              Bookburrow handles inventory and loan tracking so you can focus on
              getting children reading.
            </p>
          </div>
        </div>
        <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-forest-100/50 blur-3xl" />
      </section>

      {/* ── Plates gallery ───────────────────── */}
      <section
        ref={platesView.ref}
        className="grain relative overflow-hidden bg-forest-950 py-24 text-cream-100 md:py-36"
      >
        {/* warm glow from above */}
        <div className="absolute -left-40 -top-40 h-[28rem] w-[28rem] rounded-full bg-gold-500/[0.06] blur-3xl" />
        <div className="absolute -right-40 top-1/3 h-[32rem] w-[32rem] rounded-full bg-forest-700/25 blur-3xl" />

        <div className="relative z-10 mx-auto max-w-7xl px-6">
          {/* Section header */}
          <div className={`flex flex-col gap-4 md:flex-row md:items-end md:justify-between reveal ${platesView.isVisible ? "visible" : ""}`}>
            <div>
              <p className="font-display italic text-gold-300/80 tracking-wider">
                <span aria-hidden="true" className="mr-2">☙</span>Inside the app
              </p>
              <h2 className="mt-3 font-display text-4xl font-semibold tracking-tight sm:text-5xl md:text-6xl">
                Three plates from the library.
              </h2>
            </div>
            <p className="max-w-xs font-display italic text-sm leading-relaxed text-cream-300/60">
              Screenshots, unretouched. The product you'll actually use on Monday morning.
            </p>
          </div>

          {/* Plate 01 — Add to Library (tall, image-dominant) */}
          <div
            className={`mt-16 grid gap-10 md:mt-20 md:grid-cols-[minmax(0,0.55fr)_minmax(0,1.45fr)] md:items-center md:gap-16 reveal ${platesView.isVisible ? "visible" : ""}`}
            style={{ transitionDelay: "100ms" }}
          >
            <div className="relative">
              <div className="flex items-baseline gap-3 font-display italic">
                <span aria-hidden="true" className="text-5xl text-gold-400 md:text-6xl">№</span>
                <span className="text-xs uppercase tracking-[0.3em] text-gold-300/70">
                  Plate 01
                </span>
              </div>
              <h3 className="mt-6 font-display text-3xl font-semibold leading-tight text-cream-100 md:text-4xl">
                A new book joins the shelf.
              </h3>
              <p className="mt-6 leading-relaxed text-cream-300/80">
                Scan an ISBN and Bookburrow pulls cover, title, and author from
                Open Library. Classify the book, check the details, confirm —
                and it's in the catalogue.
              </p>
              <div className="mt-8 flex items-center gap-3">
                <span className="h-px w-8 bg-gold-400/50" />
                <p className="font-display italic text-sm text-cream-300/60">
                  Three short steps: classify, identify, confirm.
                </p>
              </div>
            </div>

            <figure className="relative">
              {/* warm paper mount behind */}
              <div className="absolute -inset-2 -rotate-[1.5deg] rounded-2xl bg-cream-100/[0.08] ring-1 ring-cream-100/15 shadow-2xl shadow-forest-950/70" />
              <div className="absolute -inset-1 rotate-[0.75deg] rounded-2xl bg-cream-100/[0.04] ring-1 ring-cream-100/10" />
              {/* the plate */}
              <div className="paper-card relative overflow-hidden rounded-2xl border border-gold-400/25 bg-cream-50 shadow-2xl shadow-forest-950/80 ring-1 ring-forest-950/40">
                <img
                  src="/screenshots/add-to-library.png"
                  alt="The Add to Library screen, showing classify, identify, and confirm steps with the book cover pulled from the ISBN lookup."
                  width={1703}
                  height={1197}
                  loading="lazy"
                  className="block w-full h-auto"
                />
              </div>
            </figure>
          </div>

          {/* Ornamental divider */}
          <div aria-hidden="true" className="mt-20 flex items-center justify-center gap-4 md:mt-24">
            <span className="h-px w-16 bg-gold-400/40" />
            <span className="font-display italic text-sm tracking-[0.3em] text-gold-400/70">
              ✦
            </span>
            <span className="h-px w-16 bg-gold-400/40" />
          </div>

          {/* Plate 02 — Books list (wide, full bleed) */}
          <div
            className={`mt-20 md:mt-24 reveal ${platesView.isVisible ? "visible" : ""}`}
            style={{ transitionDelay: "200ms" }}
          >
            <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
              <div>
                <div className="flex items-baseline gap-3 font-display italic">
                  <span aria-hidden="true" className="text-5xl text-gold-400 md:text-6xl">№</span>
                  <span className="text-xs uppercase tracking-[0.3em] text-gold-300/70">
                    Plate 02
                  </span>
                </div>
                <h3 className="mt-6 font-display text-3xl font-semibold leading-tight text-cream-100 md:text-4xl">
                  Every book, instantly findable.
                </h3>
              </div>
              <p className="max-w-sm leading-relaxed text-cream-300/80">
                Search by title, author, or accession number. Filter by status,
                category, or reading level. No pagination dread — everything's
                right there.
              </p>
            </div>

            <figure className="relative mt-12">
              <div className="absolute -inset-2 translate-x-1 translate-y-2 rotate-[0.75deg] rounded-2xl bg-cream-100/[0.08] ring-1 ring-cream-100/15 shadow-2xl shadow-forest-950/70" />
              <div className="paper-card relative overflow-hidden rounded-2xl border border-gold-400/25 bg-cream-50 shadow-2xl shadow-forest-950/80 ring-1 ring-forest-950/40">
                <img
                  src="/screenshots/books-list.png"
                  alt="The Books list, with a search bar, status/category/reading-level filters, and a table of titles with covers, authors, and on-loan status."
                  width={1710}
                  height={581}
                  loading="lazy"
                  className="block w-full h-auto"
                />
              </div>
            </figure>
          </div>

          {/* Ornamental divider */}
          <div aria-hidden="true" className="mt-20 flex items-center justify-center gap-4 md:mt-24">
            <span className="h-px w-16 bg-gold-400/40" />
            <span className="font-display italic text-sm tracking-[0.3em] text-gold-400/70">
              ✦
            </span>
            <span className="h-px w-16 bg-gold-400/40" />
          </div>

          {/* Plate 03 — Reports (wide, full bleed, copy flipped) */}
          <div
            className={`mt-20 md:mt-24 reveal ${platesView.isVisible ? "visible" : ""}`}
            style={{ transitionDelay: "300ms" }}
          >
            <div className="flex flex-col-reverse gap-6 md:flex-row md:items-end md:justify-between">
              <p className="max-w-sm leading-relaxed text-cream-300/80">
                Late books, most-borrowed titles, reading activity by year group.
                One click to print, one to export — your head teacher gets the
                story, not a spreadsheet.
              </p>
              <div className="md:text-right">
                <div className="flex items-baseline gap-3 font-display italic md:justify-end">
                  <span aria-hidden="true" className="text-5xl text-gold-400 md:text-6xl">№</span>
                  <span className="text-xs uppercase tracking-[0.3em] text-gold-300/70">
                    Plate 03
                  </span>
                </div>
                <h3 className="mt-6 font-display text-3xl font-semibold leading-tight text-cream-100 md:text-4xl">
                  Telling the story of a year.
                </h3>
              </div>
            </div>

            <figure className="relative mt-12">
              <div className="absolute -inset-2 -translate-x-1 translate-y-2 -rotate-[0.75deg] rounded-2xl bg-cream-100/[0.08] ring-1 ring-cream-100/15 shadow-2xl shadow-forest-950/70" />
              <div className="paper-card relative overflow-hidden rounded-2xl border border-gold-400/25 bg-cream-50 shadow-2xl shadow-forest-950/80 ring-1 ring-forest-950/40">
                <img
                  src="/screenshots/reports.png"
                  alt="The Reports screen, with tabs for Books on Loan, Late Books, Stock Summary, Most Popular, Reading Activity, Recently Added, and Never Lent, above a loan table with Export CSV and Print buttons."
                  width={1712}
                  height={579}
                  loading="lazy"
                  className="block w-full h-auto"
                />
              </div>
            </figure>
          </div>
        </div>
      </section>

      {/* ── Primary features ─────────────────── */}
      <section
        ref={gridView.ref}
        className="bg-cream-100 py-24 md:py-32"
      >
        <div className="mx-auto max-w-7xl px-6">
          <div className="stagger grid gap-10 lg:grid-cols-2">
            {primaryFeatures.map((f) => (
              <div
                key={f.title}
                className={`paper-card reveal group rounded-2xl border border-cream-300/60 bg-white/70 p-8 shadow-sm transition-all hover:border-forest-200 hover:shadow-md ${gridView.isVisible ? "visible" : ""}`}
              >
                <div className="flex items-start gap-5">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-forest-50 text-forest-600 transition-colors group-hover:bg-forest-100">
                    <f.icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-semibold text-forest-900">
                      {f.title}
                    </h3>
                    <p className="mt-3 leading-relaxed text-bark-600">
                      {f.desc}
                    </p>
                    <p className="mt-3 text-sm text-bark-600/70 italic">
                      {f.detail}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Secondary features ───────────────── */}
      <section
        ref={secondaryView.ref}
        className="relative bg-cream-50 py-24 md:py-32"
      >
        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <div className={`flex flex-col gap-4 md:flex-row md:items-end md:justify-between reveal ${secondaryView.isVisible ? "visible" : ""}`}>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-forest-950 sm:text-4xl">
              And all the details that matter.
            </h2>
            <p className="max-w-xs font-display italic text-sm leading-relaxed text-bark-600/70">
              The small things that add up to a system that looks after itself.
            </p>
          </div>

          <div className="stagger mt-14 grid gap-x-10 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
            {secondaryFeatures.map((f) => (
              <div
                key={f.title}
                className={`reveal flex items-start gap-4 border-t border-forest-200/60 pt-6 ${secondaryView.isVisible ? "visible" : ""}`}
              >
                <f.icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-forest-600" />
                <div>
                  <h3 className="font-display text-base font-semibold text-forest-900">
                    {f.title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-bark-600">
                    {f.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────── */}
      <section className="bg-cream-50 py-24 text-center md:py-32">
        <div className="mx-auto max-w-2xl px-6">
          <h2 className="font-display text-3xl font-semibold tracking-tight text-forest-950 sm:text-4xl">
            One less thing on your plate.
          </h2>
          <p className="mx-auto mt-4 max-w-md text-lg text-bark-600">
            &pound;149 once per school, with 12 months of updates and support.
            Currently invite-only while we work with a small group of schools.
          </p>
          <a
            href="/#waitlist"
            className="primary-action mt-8 inline-block rounded-xl bg-gold-500 px-8 py-4 text-base font-bold text-bark-900 shadow-lg shadow-gold-500/20 transition-all hover:bg-gold-400 hover:shadow-xl"
          >
            Join the waitlist
          </a>
        </div>
      </section>
    </div>
  );
}
